import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Plus, Minus, Trash2, Truck, ArrowRight, Award, Gem, Flame, CreditCard, Gift, Landmark } from "lucide-react";
import Reviews from "../components/Reviews";
import { Stars } from "../components/ProductReviewDialog";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../lib/api";
import useSeo from "../hooks/useSeo";
import { useAuth } from "../context/AuthContext";

const emptyCustomer = { name: "", email: "", phone: "", address: "", city: "", zip: "", province: "" };
const emptyBilling = { company: "", vat: "", fiscal_code: "", sdi: "" };

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [collectibles, setCollectibles] = useState([]);
  const [view, setView] = useState(new URLSearchParams(window.location.search).get("cat") === "collezione" ? "collezione" : "liquori");
  const [sort, setSort] = useState("default");
  const sorted = (list) => {
    const l = [...list];
    if (sort === "rated") return l.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0) || (b.rating_count || 0) - (a.rating_count || 0));
    if (sort === "price_asc") return l.sort((a, b) => (a.public_price || 0) - (b.public_price || 0));
    if (sort === "price_desc") return l.sort((a, b) => (b.public_price || 0) - (a.public_price || 0));
    return l;
  };
  const [settings, setSettings] = useState({ shipping_cost: 7, free_shipping_threshold: 79, enabled: true, payment_ready: false });
  const [cart, setCart] = useState([]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("cart");
  const [customer, setCustomer] = useState(emptyCustomer);
  const { user } = useAuth();
  useEffect(() => {
    if (user && (user.role === "customer" || user.role === "b2b")) {
      setCustomer((c) => (c.name || c.email ? c : { name: user.name || "", email: user.email || "", phone: user.phone || "", address: user.address || "", city: user.city || "", zip: user.zip || "", province: user.province || "" }));
    }
  }, [user]);
  const [billing, setBilling] = useState(emptyBilling);
  const [showBilling, setShowBilling] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [payMethod, setPayMethod] = useState("stripe");
  const [content, setContent] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [codeInput, setCodeInput] = useState((new URLSearchParams(window.location.search).get("amico") || "").toUpperCase());
  const amicoCode = new URLSearchParams(window.location.search).get("amico");
  const [codeBusy, setCodeBusy] = useState(false);

  useSeo({ title: "Negozio liquori artigianali online | Guatemalan Spirit",
    description: "Acquista online limoncello, meloncello, liquirizia e bottiglie numerate da collezione prodotte a Salerno. Spedizione in tutta Italia." });

  useEffect(() => {
    api.get("/products", { params: { category: "liquori" } }).then(({ data }) => setProducts((data || []).filter((p) => p.active !== false))).catch(() => {});
    api.get("/products", { params: { category: "collezione" } }).then(({ data }) => setCollectibles((data || []).filter((p) => p.active !== false))).catch(() => {});
    api.get("/settings/shop").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  const add = (p) => {
    const max = p.stock != null ? p.stock : Infinity;
    if (max <= 0) { toast.error("Esaurito"); return; }
    setCart((c) => {
      const ex = c.find((x) => x.id === p.id);
      if (ex) {
        if (ex.qty >= (ex.max ?? Infinity)) { toast.info("Hai già aggiunto tutte le bottiglie disponibili"); return c; }
        return c.map((x) => (x.id === p.id ? { ...x, qty: x.qty + 1 } : x));
      }
      return [...c, { id: p.id, name: p.name, price: p.public_price, image_url: p.image_url, format: p.format, qty: 1, max }];
    });
    toast.success(`${p.name} aggiunto al carrello`);
    if (window.matchMedia("(max-width: 640px)").matches) { setStep("cart"); setOpen(true); }
  };
  const setQty = (id, d) => setCart((c) => c.map((x) => (x.id === id ? { ...x, qty: Math.min(x.max ?? Infinity, Math.max(1, x.qty + d)) } : x)));
  const removeItem = (id) => setCart((c) => c.filter((x) => x.id !== id));

  const count = cart.reduce((s, x) => s + x.qty, 0);
  const subtotal = cart.reduce((s, x) => s + x.price * x.qty, 0);
  const threshold = Number(settings.free_shipping_threshold || 0);
  const shipping = subtotal > 0 && threshold && subtotal >= threshold ? 0 : Number(settings.shipping_cost || 0);
  const discountAmt = discount ? Math.min(discount.amount, subtotal) : 0;
  const total = subtotal + shipping - discountAmt;
  useEffect(() => { if (discount) setDiscount((d) => d && { ...d, amount: Math.round(subtotal * d.pct) / 100 }); }, [subtotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyCode = async () => {
    setCodeBusy(true);
    try {
      const { data } = await api.post("/shop/validate-code", { code: codeInput, email: customer.email || "", subtotal });
      if (data.valid) { setDiscount({ code: data.code, pct: data.pct, amount: data.amount }); toast.success(`Sconto ${data.pct}% applicato`); }
      else { setDiscount(null); toast.error(data.message || "Codice non valido"); }
    } catch { toast.error("Impossibile verificare il codice"); }
    finally { setCodeBusy(false); }
  };

  const openCart = () => { setStep("cart"); setOpen(true); };

  const payLabel = busy ? "Attendere…" : (settings.payment_ready ? `Paga ${eur(total)}` : `Invia ordine (${eur(total)})`);
  const checkout = async () => {
    if (!customer.name || !customer.email) { toast.error("Inserisci nome ed email"); return; }
    if (!customer.address || !customer.city || !customer.zip) { toast.error("Completa l'indirizzo di spedizione"); return; }
    setBusy(true);
    try {
      const payload = {
        items: cart.map((x) => ({ product_id: x.id, quantity: x.qty })),
        customer,
        billing: showBilling ? billing : null,
        notes,
        payment_method: payMethod === "bank" && settings.bank_ready ? "bank" : (settings.paypal_ready && (payMethod === "paypal" || !settings.stripe_ready) ? "paypal" : "stripe"),
        discount_code: discount?.code || "",
      };
      const { data } = await api.post("/shop/checkout", payload);
      if (data.redirect_url) { window.location.assign(data.redirect_url); return; }
      toast.success(data.message || "Ordine registrato");
      setCart([]); setOpen(false); setCustomer(emptyCustomer); setBilling(emptyBilling); setNotes("");
      window.location.assign(`/pagamento-esito?order=${data.order_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="paper-grain min-h-screen">
      <Navbar />

      <section className="max-w-7xl mx-auto px-6 pt-14 pb-6">
        <div className="eyebrow text-[#C2593F] mb-3">Shop online</div>
        <h1 className="font-serif font-semibold text-4xl lg:text-5xl text-[#0B2A45]">Le nostre bottiglie, a casa tua</h1>
        <p className="text-stone-600 mt-4 max-w-2xl">
          Acquista online i liquori artigianali della Costiera e i distillati da collezione. I fusti cocktail e i preparati per granita sono riservati agli operatori: contatta un agente.
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#0F4C81] bg-[#EFF6FF] border border-[#BFDBFE] rounded-full px-4 py-2" data-testid="shipping-note">
          <Truck className="w-4 h-4" />
          {threshold ? <>Spedizione {eur(settings.shipping_cost)} · gratis sopra {eur(threshold)}</> : <>Spedizione {eur(settings.shipping_cost)}</>}
        </div>
        {amicoCode && (
          <div className="mt-3 sm:mt-5 sm:ml-2 inline-flex items-center gap-2 text-sm text-[#0B2A45] bg-[#E0F2FE] border border-[#7DD3FC] rounded-full px-4 py-2" data-testid="amico-code-banner">
            Codice amico <b className="font-mono">{amicoCode.toUpperCase()}</b> pronto nel carrello · {settings.referral_friend_pct || 10}% sul primo ordine
          </div>
        )}
        {settings.welcome_enabled !== false && (
          <div className="mt-3 sm:mt-5 sm:ml-2 inline-flex items-center gap-2 text-sm text-[#854D0E] bg-[#FEF9C3] border border-[#FDE047] rounded-full px-4 py-2" data-testid="welcome-code-banner">
            <Gift className="w-4 h-4" />
            Primo ordine? <b className="font-mono">{settings.welcome_code || "BENVENUTO10"}</b> · -{settings.welcome_pct || 10}%
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="inline-flex bg-stone-100 rounded-full p-1" data-testid="shop-category-tabs">
          <button onClick={() => setView("liquori")} data-testid="tab-liquori"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${view === "liquori" ? "bg-[#0F4C81] text-white" : "text-stone-600 hover:text-[#0F4C81]"}`}>
            Liquori
          </button>
          <button onClick={() => setView("collezione")} data-testid="tab-collezione"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2 ${view === "collezione" ? "bg-[#1a1410] text-[#D4AF37]" : "text-stone-600 hover:text-[#9A7B1E]"}`}>
            <Gem className="w-4 h-4" />Distillati da Collezione
          </button>
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="shop-sort-select"
          className="h-10 rounded-full border border-stone-300 bg-white px-4 text-sm text-[#0B2A45] focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/30">
          <option value="default">Consigliati</option>
          <option value="rated">Più votati</option>
          <option value="price_asc">Prezzo crescente</option>
          <option value="price_desc">Prezzo decrescente</option>
        </select>
        </div>
      </section>

      {view === "liquori" && (
        <section className="max-w-7xl mx-auto px-6 pb-24">
          {products.length === 0 ? (
            <p className="text-stone-400">Nessun liquore disponibile al momento.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-grid">
              {sorted(products).map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 6) * 0.05 }}
                  className="bg-white rounded-2xl overflow-hidden border border-stone-200 flex flex-col" data-testid={`shop-card-${p.id}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                    {p.image_url ? <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="text-[0.68rem] uppercase tracking-[0.18em] font-semibold text-[#C2593F] mb-1">{p.format}</div>
                    <h3 className="font-serif text-xl font-semibold text-[#0B2A45] mb-2"><Link to={`/prodotto/${p.slug}`} className="hover:text-[#0F4C81]" data-testid={`shop-product-link-${p.id}`}>{p.name}</Link></h3>
                    {p.rating_count > 0 && <div className="flex items-center gap-1.5 mb-2 text-xs text-stone-500" data-testid={`shop-rating-${p.id}`}><Stars value={p.rating_avg} size="w-3.5 h-3.5" /><span className="font-semibold text-[#0B2A45]">{p.rating_avg}</span><span>({p.rating_count})</span></div>}
                    <p className="text-sm text-stone-500 leading-relaxed mb-4 flex-1">{p.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="font-mono font-bold text-lg text-[#0F4C81]">{eur(p.public_price)}</div>
                      <Button onClick={() => add(p)} data-testid={`add-to-cart-${p.id}`} className="bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded-full">
                        <Plus className="w-4 h-4 mr-1" />Aggiungi
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      )}

      {view === "collezione" && (
        <section className="bg-gradient-to-b from-[#14100b] to-[#0a0806] mt-2" data-testid="collection-section">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="max-w-2xl mb-12">
              <div className="text-[0.7rem] uppercase tracking-[0.3em] font-semibold text-[#D4AF37] mb-3">Edizioni rare · Pezzi numerati</div>
              <h2 className="font-serif text-3xl lg:text-4xl text-[#F5EAD0]">Distillati da Collezione</h2>
              <p className="text-stone-400 mt-4 leading-relaxed">
                Bottiglie rare e numerate, spesso in esemplare unico, con certificato di autenticità. Un patrimonio liquido per veri intenditori.
              </p>
            </div>

            {collectibles.length === 0 ? (
              <p className="text-stone-500">Nessun distillato da collezione disponibile al momento. Torna a trovarci presto.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="collection-grid">
                {sorted(collectibles).map((p, i) => {
                  const sold = p.stock != null && p.stock <= 0;
                  return (
                    <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 5) * 0.08 }}
                      className="rounded-2xl overflow-hidden border border-[#C9A227]/25 bg-gradient-to-b from-[#1c1712] to-[#100c08] flex flex-col shadow-xl" data-testid={`collection-card-${p.id}`}>
                      <div className="relative aspect-[3/4] overflow-hidden bg-black">
                        {p.image_url ? <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover" /> : null}
                        {p.limited_edition ? (
                          <span className="absolute top-3 left-3 text-[0.62rem] uppercase tracking-widest font-semibold text-[#0a0806] bg-[#D4AF37] rounded-full px-3 py-1">
                            {p.limited_edition}
                          </span>
                        ) : null}
                        {sold ? <span className="absolute inset-0 flex items-center justify-center bg-black/60 text-[#D4AF37] font-serif text-xl">Esaurito</span> : null}
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <div className="text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-[#9A7B1E] mb-1">{p.format}</div>
                        <h3 className="font-serif text-2xl text-[#F5EAD0] mb-3">{p.name}</h3>
                        <p className="text-sm text-stone-400 leading-relaxed mb-4">{p.description}</p>
                        <dl className="text-xs text-stone-400 space-y-1.5 mb-5 border-t border-[#C9A227]/15 pt-4">
                          {p.vintage ? <div className="flex justify-between"><dt className="text-stone-500">Annata</dt><dd className="text-[#E9DCC0] font-medium">{p.vintage}</dd></div> : null}
                          {p.distillery ? <div className="flex justify-between"><dt className="text-stone-500">Distilleria</dt><dd className="text-[#E9DCC0] font-medium">{p.distillery}</dd></div> : null}
                          {p.abv ? <div className="flex justify-between"><dt className="text-stone-500">Gradazione</dt><dd className="text-[#E9DCC0] font-medium">{p.abv}</dd></div> : null}
                          {p.bottle_number ? <div className="flex justify-between"><dt className="text-stone-500">N° bottiglia</dt><dd className="text-[#E9DCC0] font-medium font-mono">{p.bottle_number}</dd></div> : null}
                        </dl>
                        {p.certificate ? (
                          <div className="inline-flex items-center gap-2 text-xs text-[#D4AF37] mb-3">
                            <Award className="w-4 h-4" /> Con certificato di autenticità
                          </div>
                        ) : null}
                        {!sold && p.stock != null && p.stock <= 5 ? (
                          <div className="inline-flex items-center gap-1.5 text-[0.7rem] font-semibold uppercase tracking-wider text-[#E7B84B] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-3 py-1 mb-4 w-fit" data-testid={`collection-scarcity-${p.id}`}>
                            <Flame className="w-3.5 h-3.5" />{p.stock === 1 ? "Ultimo pezzo disponibile" : `Solo ${p.stock} pezzi rimasti`}
                          </div>
                        ) : null}
                        <div className="flex items-center justify-between mt-auto">
                          <div><div className="font-mono font-bold text-xl text-[#D4AF37]">{eur(p.public_price)}</div>
                            {p.rating_count > 0 && <div className="flex items-center gap-1.5 mt-1 text-xs text-stone-400" data-testid={`shop-rating-${p.id}`}><Stars value={p.rating_avg} size="w-3 h-3" /><span>{p.rating_avg} ({p.rating_count})</span></div>}</div>
                          <Button onClick={() => add(p)} disabled={sold} data-testid={`add-to-cart-${p.id}`}
                            className="bg-[#D4AF37] hover:bg-[#C9A227] text-[#0a0806] font-semibold rounded-full disabled:opacity-40">
                            <Plus className="w-4 h-4 mr-1" />Acquista
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      <Reviews data={content?.reviews} dark={view === "collezione"} />

      {count > 0 && (
        <button onClick={openCart} data-testid="open-cart-btn" style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
          className="fixed right-6 z-40 bg-[#C2593F] hover:bg-[#9A3412] text-white rounded-full shadow-xl px-6 h-14 flex items-center gap-3 transition-colors">
          <ShoppingBag className="w-5 h-5" />
          <span className="font-semibold" data-testid="cart-count">{count}</span>
          <span className="font-mono">{eur(total)}</span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg w-[calc(100%-2rem)] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{step === "cart" ? "Il tuo carrello" : "Dati per la spedizione"}</DialogTitle>
            <DialogDescription className="sr-only">{step === "cart" ? "Rivedi gli articoli nel carrello e procedi all'ordine" : "Inserisci i dati di spedizione e fatturazione per completare l'ordine"}</DialogDescription>
          </DialogHeader>

          {step === "cart" && (
            <div className="space-y-4">
              {cart.length === 0 && <p className="text-stone-400">Carrello vuoto.</p>}
              {cart.map((x) => (
                <div key={x.id} className="flex items-center gap-3" data-testid={`cart-item-${x.id}`}>
                  <div className="w-14 h-14 rounded-lg bg-stone-100 overflow-hidden shrink-0">
                    {x.image_url ? <img src={x.image_url} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{x.name}</div>
                    <div className="text-xs text-stone-400">{eur(x.price)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(x.id, -1)} data-testid={`cart-dec-${x.id}`} className="w-7 h-7 rounded-full border flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                    <span className="w-6 text-center text-sm font-mono">{x.qty}</span>
                    <button onClick={() => setQty(x.id, 1)} data-testid={`cart-inc-${x.id}`} className="w-7 h-7 rounded-full border flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                    <button onClick={() => removeItem(x.id)} data-testid={`cart-remove-${x.id}`} className="ml-1"><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </div>
                </div>
              ))}
              {cart.length > 0 && (
                <div className="border-t pt-4 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-stone-500">Subtotale</span><span className="font-mono">{eur(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-500">Spedizione</span><span className="font-mono">{shipping === 0 ? "Gratis" : eur(shipping)}</span></div>
                  {discount && <div className="flex justify-between text-[#15803D]" data-testid="cart-discount-line"><span>Sconto {discount.code} (-{discount.pct}%)</span><span className="font-mono">-{eur(discount.amount)}</span></div>}
                  <div className="flex justify-between text-base font-semibold pt-1"><span>Totale</span><span className="font-mono text-[#0F4C81]" data-testid="cart-total">{eur(total)}</span></div>
                </div>
              )}
              {cart.length > 0 && (settings.welcome_enabled !== false || settings.referral_enabled !== false) && (
                <div className="border-t pt-4" data-testid="discount-box">
                  {discount ? (
                    <div className="flex items-center justify-between text-sm bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                      <span className="text-green-800">Codice <b className="font-mono">{discount.code}</b> applicato</span>
                      <button onClick={() => setDiscount(null)} className="text-xs underline text-green-800" data-testid="discount-remove-btn">Rimuovi</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} placeholder={`Codice sconto (es. ${settings.welcome_code || "BENVENUTO10"})`} className="h-11 font-mono" data-testid="discount-code-input" />
                      <Button type="button" variant="outline" onClick={applyCode} disabled={!codeInput || codeBusy} className="h-11 rounded-full" data-testid="discount-apply-btn">Applica</Button>
                    </div>
                  )}
                  {!discount && <p className="text-xs text-stone-500 mt-2">Primo ordine? Usa <b className="font-mono">{settings.welcome_code || "BENVENUTO10"}</b> per il {settings.welcome_pct || 10}% di sconto.</p>}
                </div>
              )}
            </div>
          )}

          {step === "checkout" && (
            <div className="space-y-3">
              <div><Label>Nome e cognome *</Label><Input data-testid="checkout-name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="mt-1" /></div>
              {user && (user.role === "customer" || user.role === "b2b") && <p className="text-xs text-[#15803D] -mt-1" data-testid="checkout-prefilled-note">Dati precompilati dal tuo account: controlla e modifica se serve.</p>}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email *</Label><Input type="email" data-testid="checkout-email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="mt-1" /></div>
                <div><Label>Telefono</Label><Input data-testid="checkout-phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label>Indirizzo *</Label><Input data-testid="checkout-address" value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} className="mt-1" placeholder="Via, numero civico" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Città *</Label><Input data-testid="checkout-city" value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} className="mt-1" /></div>
                <div><Label>CAP *</Label><Input data-testid="checkout-zip" value={customer.zip} onChange={(e) => setCustomer({ ...customer, zip: e.target.value })} className="mt-1" /></div>
                <div><Label>Prov.</Label><Input data-testid="checkout-province" value={customer.province} onChange={(e) => setCustomer({ ...customer, province: e.target.value })} className="mt-1" /></div>
              </div>
              <button type="button" onClick={() => setShowBilling(!showBilling)} data-testid="toggle-billing" className="text-sm text-[#0F4C81] underline">
                {showBilling ? "Rimuovi dati di fatturazione" : "Aggiungi dati di fatturazione (P.IVA / CF / SDI)"}
              </button>
              {showBilling && (
                <div className="space-y-3 border rounded-xl p-3 bg-stone-50">
                  <div><Label>Ragione sociale</Label><Input data-testid="billing-company" value={billing.company} onChange={(e) => setBilling({ ...billing, company: e.target.value })} className="mt-1" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>P.IVA</Label><Input data-testid="billing-vat" value={billing.vat} onChange={(e) => setBilling({ ...billing, vat: e.target.value })} className="mt-1" /></div>
                    <div><Label>Codice Fiscale</Label><Input data-testid="billing-cf" value={billing.fiscal_code} onChange={(e) => setBilling({ ...billing, fiscal_code: e.target.value })} className="mt-1" /></div>
                  </div>
                  <div><Label>Codice SDI</Label><Input data-testid="billing-sdi" value={billing.sdi} onChange={(e) => setBilling({ ...billing, sdi: e.target.value })} className="mt-1" /></div>
                </div>
              )}
              <div><Label>Note</Label><Textarea data-testid="checkout-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" /></div>
              {(settings.stripe_ready || settings.paypal_ready || settings.bank_ready) && (
                <div data-testid="payment-method-choice">
                  <Label>Metodo di pagamento</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {settings.stripe_ready && <button type="button" onClick={() => setPayMethod("stripe")} data-testid="pay-method-stripe"
                      className={`h-12 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 ${payMethod === "stripe" ? "border-[#0F4C81] bg-[#EFF6FF] text-[#0F4C81]" : "border-stone-200 text-stone-600"}`}>
                      <CreditCard className="w-4 h-4" />Carta
                    </button>}
                    {settings.paypal_ready && <button type="button" onClick={() => setPayMethod("paypal")} data-testid="pay-method-paypal"
                      className={`h-12 rounded-xl border text-sm font-bold flex items-center justify-center ${payMethod === "paypal" ? "border-[#003087] bg-[#FFC439]/20 text-[#003087]" : "border-stone-200 text-stone-600"}`}>
                      <span className="italic"><span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span></span>
                    </button>}
                    {settings.bank_ready && <button type="button" onClick={() => setPayMethod("bank")} data-testid="pay-method-bank"
                      className={`h-12 rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 ${payMethod === "bank" ? "border-[#854D0E] bg-[#FEF9C3] text-[#854D0E]" : "border-stone-200 text-stone-600"}`}>
                      <Landmark className="w-4 h-4" />Bonifico
                    </button>}
                  </div>
                  {payMethod === "bank" && settings.bank_ready && (
                    <div className="mt-2 text-xs bg-[#FEF9C3] border border-[#FDE68A] text-[#854D0E] rounded-lg px-3 py-2" data-testid="bank-info-note">
                      Riceverai via email IBAN e causale. L'ordine viene spedito dopo la conferma del bonifico da parte nostra.
                      <div className="mt-1 font-mono text-[11px]">{settings.bank_holder} · {settings.bank_iban}</div>
                    </div>
                  )}
                </div>
              )}
              {!settings.payment_ready && (
                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-3 py-2" data-testid="payment-unconfigured-note">
                  Il pagamento online sarà attivo a breve: il tuo ordine verrà registrato e sarai ricontattato.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {step === "cart" ? (
              <Button disabled={cart.length === 0} onClick={() => setStep("checkout")} data-testid="go-checkout-btn" className="bg-[#0F4C81] text-white rounded-full">
                Procedi <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <div className="flex gap-2 w-full justify-between">
                <Button variant="outline" onClick={() => setStep("cart")} data-testid="back-to-cart-btn" className="rounded-full">Indietro</Button>
                <Button disabled={busy} onClick={checkout} data-testid="pay-btn" className="bg-[#C2593F] hover:bg-[#9A3412] text-white rounded-full">
                  {payLabel}
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
